import {
  useChangeMFAMethodMutation,
  useDeleteMFAMethodMutation,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting';
import { paths } from '@common/types';
import { useState } from 'react';
import { toast } from 'react-toastify';

export enum MFAMethod {
  APP = 'app',
  SMS = 'sms',
}

export const usePrivacy = (
  profile: paths[`/profile/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  },
) => {
  const mfaMethod = profile.mfa_type;
  const passwordLastChanged = profile.password_last_changed;

  const deleteMFAMethodMutation = useDeleteMFAMethodMutation();
  const changeMFAMethodMutation = useChangeMFAMethodMutation();

  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);

  const openChangePasswordModal = () => {
    setIsChangePasswordModalOpen(true);
  };

  const closeChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
  };

  const [isSetupAppMFAModalOpen, setIsSetupAppMFAModalOpen] = useState(false);

  const openSetupAppMFAModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSetupAppMFAModalOpen(true);
  };

  const closeSetupAppMFAModal = () => {
    setIsSetupAppMFAModalOpen(false);
  };

  const [isSetupSmsMFAModalOpen, setIsSetupSmsMFAModalOpen] = useState(false);

  const openSetupSmsMFAModal = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSetupSmsMFAModalOpen(true);
  };

  const closeSetupSmsMFAModal = () => {
    setIsSetupSmsMFAModalOpen(false);
  };

  const [isDeleteMfaAppModalOpen, setIsDeleteMfaAppModalOpen] = useState(false);
  const handleDeleteMfaAppModalOpen = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDeleteMfaAppModalOpen(true);
  };
  const handleDeleteMfaAppModalClose = () => {
    setIsDeleteMfaAppModalOpen(false);
  };

  const [isDeleteMfaSmsModalOpen, setIsDeleteMfaSmsModalOpen] = useState(false);
  const handleDeleteMfaSmsModalOpen = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDeleteMfaSmsModalOpen(true);
  };
  const handleDeleteMfaSmsModalClose = () => {
    setIsDeleteMfaSmsModalOpen(false);
  };

  const handleChangeMFAMethod = (newValue: MFAMethod) => {
    if (newValue !== mfaMethod) {
      changeMFAMethodMutation
        .mutateAsync({
          type: newValue,
        })
        .then(() => toast.success('Successfully Changed MFA Method'))
        .catch(() => toast.error('Failed to change MFA Method'));
    }
  };

  const hasMfaAppSetup = profile.has_app_mfa;
  const hasMfaSmsSetup = profile.has_sms_mfa;

  const isMfaAppSelected = mfaMethod === MFAMethod.APP;
  const isMfaSmsSelected = mfaMethod === MFAMethod.SMS;

  const handleDeleteMFAMethod = async (type: MFAMethod) => {
    try {
      await deleteMFAMethodMutation.mutateAsync({
        type,
      });
      toast.success('Successfully deleted MFA method');
      setIsDeleteMfaSmsModalOpen(false);
      setIsDeleteMfaAppModalOpen(false);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    isChangePasswordModalOpen,
    openChangePasswordModal,
    closeChangePasswordModal,
    handleChangeMFAMethod,
    mfaMethod,
    passwordLastChanged,
    hasMfaAppSetup,
    hasMfaSmsSetup,
    isMfaAppSelected,
    isMfaSmsSelected,
    handleDeleteMFAMethod,
    isSetupAppMFAModalOpen,
    openSetupAppMFAModal,
    closeSetupAppMFAModal,
    isSetupSmsMFAModalOpen,
    openSetupSmsMFAModal,
    closeSetupSmsMFAModal,
    isDeleteMfaAppModalOpen,
    handleDeleteMfaAppModalOpen,
    handleDeleteMfaAppModalClose,
    isDeleteMfaSmsModalOpen,
    handleDeleteMfaSmsModalOpen,
    handleDeleteMfaSmsModalClose,
  };
};
